import { APP_NAME } from "Constants/documentationUrls";
import { isInternalUser } from "Reducers/app/utils";

const PENDO_SESSION_KEY = "pendo_shared_session";
const SESSION_EXPIRY_HOURS = 24; // Session expires after 24 hours
const lowerCaseAppName = APP_NAME.toLowerCase();
const isFullDomain = lowerCaseAppName.includes(".");
const DOMAIN = isFullDomain
  ? `.${lowerCaseAppName}`
  : `.${lowerCaseAppName}.com`;

// Cookie utility functions
/**
 * Sets a cookie that is accessible across all upsun.com subdomains
 * @param name - Cookie name
 * @param value - Cookie value (will be URL encoded)
 * @param hours - Expiry time in hours
 */
const setCookie = (name: string, value: string, hours: number): void => {
  const maxAge = hours * 60 * 60; // Convert hours to seconds
  document.cookie = `${name}=${encodeURIComponent(value)}; domain=${DOMAIN}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

/**
 * Retrieves a cookie value by name
 * @param name - Cookie name to retrieve
 * @returns Cookie value (URL decoded) or null if not found
 */
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(";").shift();
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  }
  return null;
};

/**
 * Deletes a cookie by setting its max-age to 0
 * @param name - Cookie name to delete
 */
const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; domain=${DOMAIN}; path=/; max-age=0`;
};

export interface PendoSessionData {
  visitor: {
    id?: string;
    created_at?: string;
    roles: string[];
  };
  timestamp: number; // To check if session is still valid
  initialized: boolean;
}

/**
 * Store Pendo initialization data in cookies for cross-subdomain sharing
 * This allows Dev Center and Docs to access the same Pendo session data
 */
export const storePendoSession = (data: {
  id?: string;
  createdAt?: string;
  email?: string;
}): void => {
  const { id, createdAt, email } = data;

  const sessionData: PendoSessionData = {
    visitor: {
      id,
      created_at: createdAt,
      roles: [isInternalUser(email) ? "is_platformer" : "is_normal_account"]
    },
    timestamp: Date.now(),
    initialized: true
  };

  try {
    // Using cookies for cross-subdomain sharing
    setCookie(
      PENDO_SESSION_KEY,
      JSON.stringify(sessionData),
      SESSION_EXPIRY_HOURS
    );
  } catch (error) {
    console.error("Failed to store Pendo session data:", error); // eslint-disable-line no-console
  }
};

/**
 * Retrieve Pendo session data from cookies
 * Returns null if session doesn't exist or has expired
 */
export const getPendoSession = (): PendoSessionData | null => {
  try {
    const sessionString = getCookie(PENDO_SESSION_KEY);
    if (!sessionString) return null;

    const sessionData: PendoSessionData = JSON.parse(sessionString);

    // Check if session has expired (fallback check in case cookie expiry didn't work)
    const hoursSinceCreation =
      (Date.now() - sessionData.timestamp) / (1000 * 60 * 60);
    if (hoursSinceCreation > SESSION_EXPIRY_HOURS) {
      clearPendoSession();
      return null;
    }

    return sessionData;
  } catch (error) {
    console.error("Failed to retrieve Pendo session data:", error); // eslint-disable-line no-console
    return null;
  }
};

/**
 * Clear Pendo session data from cookies
 */
export const clearPendoSession = (): void => {
  try {
    // Using cookies for cross-subdomain sharing
    deleteCookie(PENDO_SESSION_KEY);
  } catch (error) {
    console.error("Failed to clear Pendo session data:", error); // eslint-disable-line no-console
  }
};

/**
 * Check if a valid Pendo session exists
 */
export const hasPendoSession = (): boolean => {
  return getPendoSession() !== null;
};

/**
 * Initialize Pendo from session data if available
 * This function can be used by Dev Center and Docs to initialize Pendo
 * with the same session data from Console
 */
export const initializePendoFromSession = (): boolean => {
  const sessionData = getPendoSession();

  if (!sessionData || !window.pendo) {
    return false;
  }

  try {
    window.pendo.initialize({
      visitor: sessionData.visitor,
      location: {
        transforms: [
          {
            attr: "pathname",
            action: "Replace",
            data: function (pathname: string) {
              const caseInsensitiveForwardSlash = /%2f/i;
              const replaceWith = "--";
              return pathname.replace(caseInsensitiveForwardSlash, replaceWith);
            }
          }
        ]
      }
    });

    return true;
  } catch (error) {
    console.error("Failed to initialize Pendo from session:", error); // eslint-disable-line no-console
    return false;
  }
};
